import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { LifeBuoy, MessageSquare, Send } from 'lucide-react';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { Field, Select, TextArea, TextInput } from '../components/Field';
import { PhotoInput, type UploadedItem } from '../components/PhotoInput';
import { TicketRow } from '../components/TicketRow';
import { Toast, type ToastState } from '../components/Toast';
import {
  api,
  ApiError,
  CATEGORY_LABELS,
  type TicketCategory,
  TicketCategoryEnum,
} from '../lib/api';
import { peekLauncherInitData, useWebAppContext } from '../lib/maxWebApp';
import { PageHeader } from '../components/PageHeader';

const CATEGORY_ORDER: TicketCategory[] = [
  'bug_bot',
  'bot_not_responding',
  'billing',
  'generation',
  'promo',
  'referral',
  'idea',
  'other',
];

const PRIVACY_URL = 'https://pay.arbineuro.ru/pay/privacy-policy';

export function HomePage() {
  const { ctx, authReady, waitingForMax, authError, handoffPending } = useWebAppContext();
  // Re-read on each render — exchange / postMessage may arrive after first paint.
  const launcherInitData = peekLauncherInitData();
  // Wait for `?h=` exchange — Bridge may look "ready" too early with a bad payload.
  const canCallApi =
    !handoffPending && (authReady || Boolean(launcherInitData));
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [toast, setToast] = useState<ToastState | null>(null);
  const [category, setCategory] = useState<TicketCategory | ''>('');
  const [body, setBody] = useState('');
  const [attachments, setAttachments] = useState<UploadedItem[]>([]);
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [showArchive, setShowArchive] = useState(false);

  const meQuery = useQuery({
    // Include a short fingerprint so exchange/persist triggers a fresh /api/me
    // (headers are built inside queryFn from memory — key must change after handoff).
    queryKey: ['me', canCallApi, launcherInitData.slice(0, 24)],
    queryFn: () => api.getMe(),
    enabled: canCallApi,
    retry: 1,
  });

  const sessionOk = canCallApi && meQuery.isSuccess;

  useEffect(() => {
    if (meQuery.data) {
      if (meQuery.data.email && !email) setEmail(meQuery.data.email);
      if (meQuery.data.consent_at) setConsent(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meQuery.data]);

  const activeQuery = useQuery({
    queryKey: ['tickets', 'active'],
    queryFn: () => api.listTickets('active'),
    enabled: sessionOk,
  });

  const archiveQuery = useQuery({
    queryKey: ['tickets', 'archived'],
    queryFn: () => api.listTickets('archived'),
    enabled: sessionOk && showArchive,
  });

  const submitMutation = useMutation({
    mutationFn: async () => {
      const parsedCategory = TicketCategoryEnum.safeParse(category);
      if (!parsedCategory.success) throw new Error('Выберите категорию обращения');
      const trimmed = body.trim();
      if (trimmed.length < 5) throw new Error('Опишите проблему подробнее');
      if (!consent) throw new Error('Подтвердите согласие с обработкой ПДн');
      if (!email.trim() || !/^\S+@\S+\.\S+$/.test(email.trim())) {
        throw new Error('Укажите корректный email');
      }

      const currentEmail = meQuery.data?.email;
      const consentAlready = Boolean(meQuery.data?.consent_at);
      if (email.trim() !== currentEmail || !consentAlready) {
        await api.updateMe({ email: email.trim(), consent: true });
      }

      const res = await api.createTicket({
        category: parsedCategory.data,
        body: trimmed,
        attachment_tokens: attachments.map((a) => a.token),
      });
      return res.ticket;
    },
    onSuccess: (ticket) => {
      qc.invalidateQueries({ queryKey: ['tickets'] });
      qc.invalidateQueries({ queryKey: ['me'] });
      setBody('');
      setCategory('');
      setAttachments([]);
      setToast({ kind: 'success', message: `Тикет #${ticket.id} создан` });
      navigate(`/tickets/${ticket.id}`);
    },
    onError: (err: unknown) => {
      const msg =
        err instanceof ApiError
          ? mapApiError(err)
          : err instanceof Error
            ? err.message
            : 'Не удалось создать обращение';
      setToast({ kind: 'error', message: msg });
    },
  });

  if ((waitingForMax || handoffPending) && !canCallApi) {
    return (
      <main className="page-shell">
        <PageHeader title="Поддержка" subtitle="Подключение…" />
        <div className="page-shell__body">
          <p className="text-center text-[14px] text-ink-muted py-6">
            Получаем данные входа. Пока их нет — запрос на сервер не отправляется.
          </p>
        </div>
      </main>
    );
  }

  if (authError && !canCallApi) {
    return (
      <main className="page-shell">
        <PageHeader title="Поддержка" />
        <div className="page-shell__body">
          <Card title="Не удалось войти" subtitle={authError} icon={<LifeBuoy size={18} />}>
            {null}
          </Card>
        </div>
      </main>
    );
  }

  if (!canCallApi) {
    return (
      <main className="page-shell">
        <PageHeader title="Поддержка" />
        <div className="page-shell__body">
          <Card
            title="Поддержка недоступна"
            subtitle="Похоже, вы открыли страницу вне бота. Вернитесь в бот и нажмите кнопку «Поддержка» ещё раз."
            icon={<LifeBuoy size={18} />}
          >
            {!ctx.devBypass && (
              <p className="text-[12px] text-ink-muted mt-2">
                Откройте поддержку из бота (кнопка / deep link). Тестовый вход через длинный{' '}
                <code>init_data</code> в URL больше не является основным путём.
              </p>
            )}
          </Card>
        </div>
      </main>
    );
  }

  if (meQuery.isPending) {
    return (
      <main className="page-shell">
        <PageHeader title="Поддержка" subtitle="Проверяем вход…" />
        <div className="page-shell__body">
          <p className="text-center text-[14px] text-ink-muted py-6">Загрузка…</p>
        </div>
      </main>
    );
  }

  if (meQuery.isError) {
    const msg = mapMeQueryError(meQuery.error);
    return (
      <main className="page-shell">
        <PageHeader title="Поддержка" />
        <div className="page-shell__body">
          <Card title="Не удалось войти" subtitle={msg} icon={<LifeBuoy size={18} />}>
            {null}
          </Card>
        </div>
      </main>
    );
  }

  if (!sessionOk) {
    return (
      <main className="page-shell">
        <PageHeader title="Поддержка" subtitle="Подготавливаем форму…" />
        <div className="page-shell__body">
          <p className="text-center text-[14px] text-ink-muted py-6">Загрузка…</p>
        </div>
      </main>
    );
  }

  return (
    <main className="page-shell">
      <Toast toast={toast} onDismiss={() => setToast(null)} />

      <PageHeader
        title="Поддержка"
        subtitle="Опишите вопрос — мы ответим в ближайшее время."
      />

      <div className="page-shell__body">
      <Card
        title="Новое обращение"
        icon={<LifeBuoy size={18} />}
        subtitle="Мы связываемся с вами через бот и email."
      >
        <div className="flex flex-col gap-4">
          <Field label="Категория" htmlFor="ticket-category">
            <Select
              id="ticket-category"
              value={category}
              onChange={(e) => setCategory(e.target.value as TicketCategory | '')}
            >
              <option value="">— выберите —</option>
              {CATEGORY_ORDER.map((c) => (
                <option key={c} value={c}>
                  {CATEGORY_LABELS[c]}
                </option>
              ))}
            </Select>
          </Field>

          <Field
            label="Сообщение"
            htmlFor="ticket-body"
            hint="Если столкнулись с багом — прикрепите скриншот, поможем быстрее."
          >
            <TextArea
              id="ticket-body"
              value={body}
              maxLength={1000}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Расскажите как можно подробнее…"
            />
            <div className="text-right text-[11px] text-ink-faint">
              {body.length}/1000
            </div>
          </Field>

          <Field label="Скриншоты" optional>
            <PhotoInput
              value={attachments}
              onChange={setAttachments}
              onError={(m) => setToast({ kind: 'error', message: m })}
            />
          </Field>

          <Field
            label="Email"
            htmlFor="ticket-email"
            hint="На эту почту придёт уведомление о новом ответе поддержки."
          >
            <TextInput
              id="ticket-email"
              // text + inputMode — меньше прозрачного Google autofill, чем type=email
              type="text"
              inputMode="email"
              autoComplete="off"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              name="support_contact_email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="имя@yandex.ru"
            />
          </Field>

          <label className="flex items-start gap-3 text-[14px] text-ink leading-snug cursor-pointer py-1 -webkit-tap-highlight-color-transparent">
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              className="mt-0.5 h-6 w-6 min-h-[24px] min-w-[24px] shrink-0 rounded border-line text-accent focus:ring-accent/30 accent-accent"
            />
            <span>
              Я согласен с обработкой персональных данных в соответствии с{' '}
              <a href={PRIVACY_URL} target="_blank" rel="noreferrer">
                политикой обработки ПДн
              </a>
              .
            </span>
          </label>

          <div className="form-submit-bar">
            <Button
              leftIcon={<Send size={16} />}
              loading={submitMutation.isPending}
              onClick={() => submitMutation.mutate()}
              fullWidth
              className="min-h-[48px] text-[16px]"
            >
              Отправить запрос
            </Button>
          </div>
        </div>
      </Card>

      <Card
        title="История обращений"
        icon={<MessageSquare size={18} />}
        subtitle="Архив автоматически очищается через 6 месяцев."
      >
        <div className="segmented-tabs mb-4" role="tablist" aria-label="Фильтр обращений">
          <button
            type="button"
            role="tab"
            aria-selected={!showArchive}
            className={`segmented-tabs__btn${!showArchive ? ' is-active' : ''}`}
            onClick={() => setShowArchive(false)}
          >
            Активные
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={showArchive}
            className={`segmented-tabs__btn${showArchive ? ' is-active' : ''}`}
            onClick={() => setShowArchive(true)}
          >
            Архив
          </button>
        </div>

        {!showArchive ? (
          <TicketSection
            tickets={activeQuery.data?.tickets ?? []}
            loading={activeQuery.isPending}
            emptyMessage="Пока нет активных обращений."
          />
        ) : (
          <TicketSection
            tickets={archiveQuery.data?.tickets ?? []}
            loading={archiveQuery.isPending}
            emptyMessage="Архивных обращений нет."
          />
        )}
      </Card>
      </div>
    </main>
  );
}

function TicketSection({
  tickets,
  loading,
  emptyMessage,
}: {
  tickets: Array<{
    id: string;
    bot_id: string;
    category: TicketCategory;
    status:
      | 'open'
      | 'in_progress'
      | 'waiting_user'
      | 'resolved'
      | 'archived';
    subject: string | null;
    created_at: string;
    updated_at: string;
    closed_at: string | null;
  }>;
  loading: boolean;
  emptyMessage: string;
}) {
  return (
    <div>
      {loading ? (
        <p className="py-6 text-center text-[13px] text-ink-muted">Загружаем…</p>
      ) : tickets.length === 0 ? (
        <p className="py-4 text-[14px] text-ink-muted">{emptyMessage}</p>
      ) : (
        <ul className="flex flex-col">
          {tickets.map((t) => (
            <li key={t.id}>
              <TicketRow ticket={t} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function mapAuthError(err: ApiError): string {
  if (err.message.includes('hash_') || err.message.includes('init_data')) {
    return 'Параметр init_data в URL недействителен. Нужна криптоподпись от MAX, а не id пользователя (не «123»).';
  }
  if (err.message.includes('invalid_signature') || err.code === 'invalid_signature') {
    return 'Подпись MAX не совпала с bot_id. Нужен webapp_secret того бота, из которого открыли miniapp (или не передавайте чужой bot_id).';
  }
  if (err.message.includes('missing') && err.message.includes('Init-Data')) {
    return 'Нет initData от MAX Bridge — откройте поддержку снова из бота.';
  }
  return err.message;
}

function mapMeQueryError(err: unknown): string {
  if (err instanceof ApiError) return mapAuthError(err);
  if (err instanceof Error) {
    if (err.message.includes('ISO-8859-1') || err.message.includes('non ISO')) {
      return 'Данные входа содержат недопустимые символы в заголовке. Обновите приложение поддержки и откройте снова из бота.';
    }
    return err.message;
  }
  return 'Не удалось проверить вход';
}

function mapApiError(err: ApiError): string {
  const map: Record<string, string> = {
    consent_required: 'Сначала подтвердите согласие с обработкой ПДн',
    too_many_attachments: 'Максимум 3 файла',
    file_too_large: 'Файл больше 3 МБ',
    token_expired: 'Загрузка устарела — прикрепите файл заново',
    token_invalid: 'Не удалось привязать файл — попробуйте ещё раз',
    invalid_body: 'Проверьте заполнение полей',
  };
  return map[err.code] ?? err.message;
}
